<template>
  <!--
    One app in the shell's sidebar (design 1b / 1g). Three looks:
      wide   34px row: icon + label + count/dot; hover shows the grip and the pin button (editable)
      icons  40x40 tile (collapsed desktop rail) with a dark tooltip on hover / focus
      rail   tablet tile (1g): icon pill + short name underneath, 44px+ target, no tooltip
    The row is a real link (router-link when the app has a route), so clicking navigates; the row
    also emits `select` so the shell can close its popovers. Never reads $route.
  -->
  <div
    class="app-nav-item"
    :class="[`app-nav-item--${variant}`, { 'is-active': active, 'is-editable': editable }]"
    :data-test="`app-nav-${item.id}`"
    :data-id="item.id"
    @click="onRowClick"
    @mouseenter="showTip"
    @mouseleave="hideTip"
    @focusin="showTip"
    @focusout="hideTip"
  >
    <span v-if="editable && variant === 'wide'" class="app-nav-item__grip" aria-hidden="true">
      <AppIcon name="grip" :size="13" :stroke="2.6" />
    </span>

    <component
      :is="item.to ? 'router-link' : 'a'"
      class="app-nav-item__link"
      v-bind="linkAttrs"
      :aria-current="active ? 'page' : null"
      :aria-label="variant === 'wide' ? null : item.label"
      draggable="false"
    >
      <template v-if="variant === 'rail'">
        <span class="app-nav-item__pill">
          <AppIcon :name="item.icon" :size="21" />
          <span v-if="countText" class="app-nav-item__badge" data-test="app-nav-count">{{ countText }}</span>
          <span v-else-if="item.dot" class="app-nav-item__dot" data-test="app-nav-dot" />
        </span>
        <span class="app-nav-item__short" data-test="app-nav-short">{{ item.shortLabel || item.label }}</span>
      </template>

      <template v-else-if="variant === 'icons'">
        <AppIcon :name="item.icon" :size="20" />
      </template>

      <template v-else>
        <span class="app-nav-item__icon"><AppIcon :name="item.icon" :size="18" /></span>
        <span class="app-nav-item__label" data-test="app-nav-label">{{ item.label }}</span>
        <span v-if="countText" class="app-nav-item__meta app-nav-item__count" data-test="app-nav-count">{{ countText }}</span>
        <span v-else-if="item.dot" class="app-nav-item__meta app-nav-item__meta-dot" data-test="app-nav-dot" />
      </template>
    </component>

    <!-- icons tile: badge / dot sit on the tile corner, outside the 40x40 link -->
    <template v-if="variant === 'icons'">
      <span v-if="countText" class="app-nav-item__badge" data-test="app-nav-count">{{ countText }}</span>
      <span v-else-if="item.dot" class="app-nav-item__dot" data-test="app-nav-dot" />
      <span
        v-show="tipOn"
        class="app-nav-item__tip"
        role="presentation"
        data-test="app-nav-tip"
        :style="{ top: `${tipY}px` }"
      >{{ item.label }}</span>
    </template>

    <button
      v-if="editable && variant === 'wide'"
      type="button"
      class="app-nav-item__pin"
      :data-test="`app-nav-pin-${item.id}`"
      :title="pinTitle"
      :aria-label="`${pinTitle}: ${item.label}`"
      @click.stop.prevent="$emit('pin-toggle', item.id)"
    >
      <AppIcon :name="pinned ? 'pinOff' : 'pin'" :size="15" />
    </button>
  </div>
</template>

<script>
import AppIcon from './AppIcon.vue';

/** Count pill text: a positive number, capped at "99+"; '' otherwise (then the dot may show). */
export function countLabel(count) {
  if (typeof count !== 'number' || !isFinite(count) || count <= 0) return '';
  return count > 99 ? '99+' : String(Math.floor(count));
}

export default {
  name: 'AppNavItem',
  components: { AppIcon },
  props: {
    /** Resolved app row { id, label, shortLabel, icon, to, count?, dot? }. */
    item: { type: Object, required: true },
    active: { type: Boolean, default: false },
    /** In the Pinned list (pin button then offers "Move to More apps"). */
    pinned: { type: Boolean, default: false },
    variant: { type: String, default: 'wide', validator: v => ['wide', 'icons', 'rail'].includes(v) },
    /** Admin / Super Admin: grip + pin button. */
    editable: { type: Boolean, default: false },
  },
  data() {
    return { tipOn: false, tipY: 0 };
  },
  computed: {
    // Never pass `href` to router-link, not even as undefined: a parent's attrs override the
    // child root's, so it erased router-link's own href (rows were unfocusable, no new-tab).
    linkAttrs() {
      return this.item.to ? { to: this.item.to } : { href: '#' };
    },
    countText() {
      return countLabel(this.item.count);
    },
    pinTitle() {
      return this.pinned ? 'Move to More apps' : 'Pin to sidebar';
    },
  },
  methods: {
    onRowClick(evt) {
      // The pin button stops its own click; anything else on the row is the link.
      if (!this.item.to && evt && typeof evt.preventDefault === 'function') evt.preventDefault();
      this.$emit('select', this.item);
    },
    showTip() {
      if (this.variant !== 'icons') return;
      // The rail scrolls (overflow clips absolute children), so the tooltip is position:fixed and
      // placed from the tile's on-screen box.
      const rect = this.$el && this.$el.getBoundingClientRect ? this.$el.getBoundingClientRect() : null;
      this.tipY = rect ? Math.round(rect.top + rect.height / 2) : 0;
      this.tipOn = true;
    },
    hideTip() {
      this.tipOn = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

.app-nav-item {
  position: relative;
  flex: none;
  @include fu-font-base;
}

.app-nav-item__link {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  user-select: none;
  -webkit-user-drag: none;
  &:hover,
  &:focus {
    text-decoration: none;
    color: inherit;
  }
  @include fu-focus-ring;
}

// ---- wide row (1b) -----------------------------------------------------------------------------
.app-nav-item--wide {
  display: flex;
  align-items: center;
  height: $shell-row-height;
  padding: 0 6px 0 0;
  border-radius: 8px;
  color: $fu-neutral-700;
  background: transparent;

  .app-nav-item__link {
    flex: 1;
    min-width: 0;
    height: 100%;
    gap: 10px;
    padding-left: 10px;
    border-radius: 8px;
  }
  .app-nav-item__icon {
    display: flex;
    color: $fu-neutral-500;
  }
  .app-nav-item__label {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: $fu-fw-medium;
    @include fu-ellipsis;
  }
  .app-nav-item__count {
    padding-right: 4px;
    font-size: 12.5px;
    font-weight: $fu-fw-bold;
    color: $fu-neutral-500;
    @include fu-tabular;
  }
  .app-nav-item__meta-dot {
    width: 7px;
    height: 7px;
    margin-right: 6px;
    border-radius: 50%;
    background: $fu-brand-500;
    flex: none;
  }

  &:hover {
    background: $fu-neutral-50;
  }
  &.is-active {
    background: $fu-neutral-100;
    color: $fu-text;
    .app-nav-item__label { font-weight: $fu-fw-bold; }
    .app-nav-item__icon { color: $fu-brand; }
  }

  // Hover / keyboard focus swaps the count for the pin button (design: metaCnt && !hover).
  &.is-editable:hover,
  &.is-editable:focus-within {
    .app-nav-item__meta { display: none; }
    .app-nav-item__pin { display: flex; }
    .app-nav-item__grip { display: flex; }
  }
}

.app-nav-item__grip {
  position: absolute;
  left: -9px;
  top: 10px;
  display: none;
  color: $fu-neutral-300;
  pointer-events: none;
}

.app-nav-item__pin {
  @include fu-button-reset;
  width: 24px;
  height: 24px;
  flex: none;
  border-radius: 6px;
  display: none;
  align-items: center;
  justify-content: center;
  color: $fu-neutral-500;
  cursor: pointer;
  &:hover {
    background: $fu-neutral-200;
    color: $fu-text;
  }
  @include fu-focus-ring;
  &:focus-visible { display: flex; }
}

// ---- icons tile (collapsed desktop) ------------------------------------------------------------
.app-nav-item--icons {
  width: 40px;
  height: 40px;

  .app-nav-item__link {
    width: 40px;
    height: 40px;
    justify-content: center;
    border-radius: 10px;
    color: $fu-neutral-600;
    background: transparent;
    &:hover {
      background: $fu-neutral-100;
      color: $fu-text;
    }
  }
  &.is-active .app-nav-item__link {
    background: $fu-brand-50;
    color: $fu-brand-700;
  }
  .app-nav-item__badge {
    position: absolute;
    top: 0;
    right: -3px;
    @include shell-count-badge;
  }
  .app-nav-item__dot {
    position: absolute;
    top: 7px;
    right: 7px;
    @include shell-dot;
  }
}

.app-nav-item__tip {
  position: fixed;
  left: $shell-sidebar-rail - 2px; // design: 50px from a 40px tile that starts 12px into the rail
  transform: translateY(-50%);
  z-index: $shell-z-popover;
  @include shell-tooltip;
}

// ---- tablet rail tile (1g) ---------------------------------------------------------------------
.app-nav-item--rail {
  width: 60px;

  .app-nav-item__link {
    width: 60px;
    min-height: 52px;
    flex-direction: column;
    justify-content: center;
    gap: 3px;
    padding: 2px 0;
    border-radius: 12px;
    color: $fu-neutral-600;
    -webkit-tap-highlight-color: transparent;
  }
  .app-nav-item__pill {
    position: relative;
    width: 44px;
    height: 28px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
  }
  .app-nav-item__short {
    max-width: 60px;
    font-size: 11px;
    font-weight: $fu-fw-semibold;
    line-height: 1.15;
    @include fu-ellipsis;
  }
  .app-nav-item__badge {
    position: absolute;
    top: -4px;
    right: -2px;
    @include shell-count-badge;
  }
  .app-nav-item__dot {
    position: absolute;
    top: 2px;
    right: 8px;
    @include shell-dot;
  }
  &.is-active {
    .app-nav-item__pill {
      background: $fu-brand-50;
      color: $fu-brand-700;
    }
    .app-nav-item__short {
      color: $fu-text;
      font-weight: $fu-fw-bold;
    }
  }
}
</style>
